<!--
  The whole document, editable, with the tools at the top (TASK-050 phase 2).

  Euan, having used the first version: "it's not really what I want the end
  product to be like. I want to edit the entire document without it being in a
  form, much like how Google Docs work and how the web-based Claude.ai
  artifacts work. There, you can edit the entire document, and the tools sit at
  the top while you work. Yes, you can still lock a paragraph for paragraph."

  So: one ProseMirror view over the whole tab, a sticky toolbar, and the lock
  following the cursor instead of a button opening a box.

  ==================================================================
  HOW A DOCUMENT EDITOR PRODUCES BLOCK OPS
  ==================================================================
  It does not translate keystrokes. ProseMirror emits steps at character
  granularity and the op vocabulary is block-granular; SPECIFICATION §6 spells
  out why bridging those directly does not work. Instead the editor waits for
  the typing to stop and asks which BLOCKS are different now -- a question with
  an exact answer, because blocks carry stable ids -- and sends `replace`,
  `insert` and `delete` for those. See `diff.ts`.

  The paragraph nobody touched produces no op, so its comment anchors never
  move. That is the whole reason the save is a diff rather than a dump.

  ==================================================================
  WHAT `before` IS, AND WHY IT IS NOT WHAT IS ON SCREEN
  ==================================================================
  `serverBlocks` is the document as the SERVER last gave it, hashes and all.
  Every guard sent with a replace comes from there. Keeping a separate copy --
  rather than diffing against whatever the editor rendered last -- is what
  makes the guard mean anything: a hash taken from our own optimistic state
  would always match, and would protect nobody from an agent writing to the
  same paragraph.
-->
<script lang="ts">
  import { onDestroy, onMount, untrack } from 'svelte'
  import { EditorState, TextSelection, type Transaction } from 'prosemirror-state'
  import { EditorView } from 'prosemirror-view'
  import { Node as PMNode } from 'prosemirror-model'
  import { blockSchema, toStored, TOGGLEABLE, type Toggleable } from './schema.ts'
  import {
    editorPlugins,
    headingAt,
    insertRule,
    makeCodeBlock,
    makeList,
    markActive,
    setHeading,
    setLink,
    toggle,
    toggleQuote,
  } from './commands.ts'
  import { diffBlocks, type Block } from './diff.ts'
  import { DiagramView } from './DiagramView.ts'
  import { useHighlights } from '../highlights.svelte'
  import type { BlockLocks } from './locks.svelte.ts'

  let {
    blocks,
    rev,
    locks,
    onsave,
    onblockchange,
  }: {
    /** The tab's blocks, as the server last gave them. */
    blocks: Block[]
    /** The revision those blocks came from, so a remote write can be spotted. */
    rev: number
    locks: BlockLocks
    /** Send these ops. Returns true if they landed. */
    onsave: (ops: ReturnType<typeof diffBlocks>) => Promise<boolean>
    /** The block the cursor is in, for the lock. */
    onblockchange: (blockId: string | null) => void
  } = $props()

  /** Marks a transaction that came from the server rather than from typing. */
  const REMOTE = 'unomena-remote'

  /** Milliseconds of quiet before a save. Long enough that a sentence is one
      write, short enough that nobody wonders whether it saved. */
  const SAVE_AFTER_MS = 900

  let host = $state<HTMLDivElement | null>(null)
  let view: EditorView | null = null
  /** The document as the server last gave it. The guard's source of truth. */
  let serverBlocks: Block[] = []
  let serverRev = 0
  let saveTimer: ReturnType<typeof setTimeout> | null = null
  let cursorBlock: string | null = null
  /**
   * Blocks THIS browser has changed since the last successful save.
   *
   * The save writes only these. It is not an optimisation: the editor keeps
   * typing while a remote revision arrives, so its view of a paragraph
   * somebody else edited can be older than the server's -- and a diff over
   * everything would read that as a local edit and write their words back to
   * what they said before.
   */
  let touched = new Set<string>()
  /**
   * The diagram node views on screen, by block id.
   *
   * Comment threads on a diagram are drawn by tinting the labels mermaid
   * produced, which only the node view can reach -- ProseMirror owns the
   * element and mermaid owns everything inside it. So the views are kept here
   * and told when the ranges change, the same way `MermaidBlock` is told in
   * the read view. BUG-028.
   */
  const diagrams = new Map<string, DiagramView>()
  const hl = useHighlights()

  let marks = $state<Record<string, boolean>>({})
  let heading = $state<number | null>(null)
  let dirty = $state(false)
  let saving = $state(false)
  let menuOpen = $state(false)

  /**
   * A block as a reader sees it: no `h`, no `rev`.
   *
   * Those two are how the server guards writes and numbers revisions. They
   * change whenever a block is written, including by a write that leaves the
   * words exactly as they were, so they must not count towards "has this
   * document changed?".
   */
  function visible(blocks: Block[]): unknown {
    const strip = (v: unknown): unknown => {
      if (Array.isArray(v)) return v.map(strip)
      if (!v || typeof v !== 'object') return v
      const out: Record<string, unknown> = {}
      for (const [k, val] of Object.entries(v as Record<string, unknown>)) {
        if (k === 'h' || k === 'rev') continue
        out[k] = strip(val)
      }
      return out
    }
    return strip(blocks)
  }

  function currentBlocks(): Block[] {
    const json = view!.state.doc.toJSON() as { content?: Block[] }
    return toStored(JSON.parse(JSON.stringify(json.content ?? []))) as Block[]
  }

  /** The id of the top-level block the cursor sits in. */
  function blockAtCursor(state: EditorState): string | null {
    // `state.selection.$from` read through a property, not destructured:
    // Svelte reserves the `$` prefix for its own runes and refuses a local
    // binding that starts with one.
    const at = state.selection.$from
    if (at.depth === 0) return null
    const top = at.node(1)
    const id = top?.attrs?.id
    return typeof id === 'string' && id ? id : null
  }

  /**
   * Update the toolbar, at most once a frame.
   *
   * It used to run synchronously inside `dispatchTransaction`, and a mouse
   * drag across a paragraph produces a transaction per mousemove -- so
   * selecting a few words forced a dozen Svelte re-renders in the middle of
   * the gesture, and Chromium abandoned the selection. The words never
   * highlighted and the Comment button never appeared, which is what Euan
   * reported: "when I highlight a word, the comment button doesn't appear
   * anymore."
   *
   * Coalescing to one update a frame fixes it and is what a toolbar wants
   * anyway: nobody can read six buttons changing thirteen times in a drag.
   */
  let chromeFrame = 0
  function refreshChrome() {
    cancelAnimationFrame(chromeFrame)
    chromeFrame = requestAnimationFrame(applyChrome)
  }

  function applyChrome() {
    if (!view) return
    const next: Record<string, boolean> = {}
    for (const name of TOGGLEABLE) next[name] = markActive(view.state, name)
    next.link = markActive(view.state, 'link')
    marks = next
    heading = headingAt(view.state)
  }

  /**
   * Refuse a change that would touch a block somebody else is holding.
   *
   * This is the soft lock, enforced at the one place a person can feel it --
   * the keystroke -- rather than only when the save is refused. Being told
   * "that paragraph is Euan's" after typing a sentence into it is worse than
   * not being able to type it.
   *
   * Only blocks held by SOMEBODY ELSE. `locks.held` never contains our own.
   */
  function touchesHeldBlock(tr: Transaction): string | null {
    if (!tr.docChanged || !view) return null
    const held = locks.held
    if (!Object.keys(held).length) return null
    let hit: string | null = null
    // Positions in the OLD document, which is where the held ids live.
    tr.steps.forEach((step, i) => {
      if (hit) return
      const map = tr.mapping.maps[i]
      map.forEach((from, to) => {
        if (hit) return
        view!.state.doc.nodesBetween(Math.max(0, from), Math.min(view!.state.doc.content.size, to), (node, pos, parent) => {
          if (hit || parent !== view!.state.doc) return
          const id = node.attrs?.id
          if (typeof id === 'string' && held[id]) hit = id
        })
      })
    })
    return hit
  }

  let blockedBy = $state<string | null>(null)

  /** Remember every top-level block a change reaches into. */
  function noteTouched(tr: Transaction) {
    if (!view) return
    const doc = view.state.doc
    tr.steps.forEach((step, i) => {
      const map = tr.mapping.maps[i]
      map.forEach((from, to) => {
        const lo = Math.max(0, Math.min(from, doc.content.size))
        const hi = Math.max(0, Math.min(to, doc.content.size))
        doc.nodesBetween(lo, hi, (node, _pos, parent) => {
          if (parent !== doc) return
          const id = node.attrs?.id
          if (typeof id === 'string' && id) touched.add(id)
        })
      })
    })
    // The block the caret is in, which a pure insertion at its edge can miss.
    const at = blockAtCursor(view.state)
    if (at) touched.add(at)
  }

  function queueSave() {
    if (saveTimer) clearTimeout(saveTimer)
    saveTimer = setTimeout(() => void save(), SAVE_AFTER_MS)
  }

  export async function save(): Promise<boolean> {
    if (!view || saving) return false
    if (saveTimer) {
      clearTimeout(saveTimer)
      saveTimer = null
    }
    const after = currentBlocks()
    const mine = new Set(touched)
    const ops = diffBlocks(serverBlocks, after, mine)
    if (!ops.length) {
      dirty = false
      return true
    }
    saving = true
    try {
      const ok = await onsave(ops)
      if (ok) {
        // Only what was just written is forgotten. Anything typed WHILE the
        // request was in flight is still ours to save on the next pass.
        for (const id of mine) touched.delete(id)
        // Not `after`: the server mints ids and recomputes hashes, so the
        // next diff has to be against what it actually stored. The parent
        // hands those back through `blocks`.
        dirty = false
      }
      return ok
    } finally {
      saving = false
    }
  }

  onMount(() => {
    if (!host) return
    serverBlocks = JSON.parse(JSON.stringify(blocks))
    serverRev = rev
    const doc = PMNode.fromJSON(blockSchema, { type: 'doc', content: blocks })
    view = new EditorView(host, {
      state: EditorState.create({ doc, plugins: editorPlugins(() => view?.dom.blur()) }),
      dispatchTransaction(tr) {
        if (!view) return
        const held = touchesHeldBlock(tr)
        if (held) {
          blockedBy = held
          setTimeout(() => (blockedBy = null), 2500)
          return
        }
        ;(window as any).__tx = ((window as any).__tx ?? 0) + 1
        if ((window as any).__tx < 30)
          console.log(`[tx] ${(window as any).__tx} from=${tr.selection.from} to=${tr.selection.to} doc=${tr.docChanged} selSet=${tr.selectionSet} steps=${tr.steps.length}`)
        const remote = tr.getMeta(REMOTE) === true
        // Which top-level blocks this change lands in, read from the doc
        // BEFORE it is applied, which is where the old ids live.
        if (tr.docChanged && !remote) noteTouched(tr)
        view.updateState(view.state.apply(tr))
        if (tr.docChanged && !remote) {
          dirty = true
          queueSave()
        }
        const at = blockAtCursor(view.state)
        if (at !== cursorBlock) {
          cursorBlock = at
          onblockchange(at)
        }
        refreshChrome()
      },
      attributes: { class: 'pm-doc', 'aria-label': 'Document' },
      /*
       * THE LOCK FOLLOWS FOCUS, NOT JUST THE CARET.
       *
       * The claim is made when the caret enters a block and given up when it
       * enters another -- which held a paragraph for ever if the caret never
       * moved on. Selecting a phrase to comment on does exactly that: the
       * selection stays in the document while the person types in the
       * sidebar, so the block stayed claimed, renewed every eight seconds,
       * and an agent asked to act on that very paragraph was refused with
       * "Euan Jonker is editing that block right now" while he was doing
       * nothing of the kind. He reported it in those words: "I highlighted
       * the text and commented on it and clicked the button. I'm not editing
       * it."
       *
       * A caret in an editor nobody is looking at is not somebody editing. So
       * the claim is dropped on blur and taken again on focus.
       */
      handleDOMEvents: {
        blur: () => {
          void locks.release()
          return false
        },
        focus: (v) => {
          const at = blockAtCursor(v.state)
          if (at) void locks.claim(at)
          return false
        },
      },
      nodeViews: {
        codeBlock: (node, view, getPos) => {
          const dv = new DiagramView(node, view, getPos)
          const id = node.attrs.id as string | null
          if (id) {
            diagrams.set(id, dv)
            // Whatever is already known about this block, immediately: a
            // document opened on a diagram that has threads should show them
            // without waiting for anything to change.
            dv.setComments(hl?.rangesFor(id) ?? [], hl?.active ?? null, hl)
          }
          return dv
        },
      },
    })
    refreshChrome()
  })

  /**
   * The document changed on the server.
   *
   * Only adopted when there is nothing unsaved here. Replacing the editor's
   * state under somebody mid-sentence would take their words and their cursor
   * with it -- and the soft lock means the paragraph they are in is not one
   * the incoming change can have touched anyway. While dirty, the new revision
   * waits; the next save's guard is what catches a genuine collision.
   */
  $effect(() => {
    const nextRev = rev
    const nextBlocks = blocks
    untrack(() => {
      if (!view || nextRev === serverRev) return
      /*
       * A first revision is not a remote change.
       *
       * If this mounted before the revision had been set, `serverRev` is 0 and
       * the real one arrives looking like somebody else's write. Record it and
       * move on: what is on screen came from those very blocks.
       */
      if (serverRev === 0) {
        serverBlocks = JSON.parse(JSON.stringify(nextBlocks))
        serverRev = nextRev
        return
      }
      serverBlocks = JSON.parse(JSON.stringify(nextBlocks))
      serverRev = nextRev
      if (dirty || saving) return
      /*
       * Adopt the server's version as a TRANSACTION, never as a new state.
       *
       * This used to build a fresh `EditorState` and hand it to
       * `view.updateState`. That is the wrong tool twice over. A new state has
       * a new, default selection -- the very start of the document -- so
       * anybody typing lost their place. Worse, replacing the state makes
       * ProseMirror rewrite the DOM selection and SUPPRESS the next
       * `selectionchange`, and if that lands on a click the click is eaten:
       * the caret appears to move, ProseMirror still thinks it is at position
       * 1, and the next keystroke edits the top of the document. That is
       * BUG-029, and it is why the browser suite kept reporting a paragraph
       * typed into the title.
       *
       * A transaction replaces the content and MAPS the selection through the
       * change, which is what ProseMirror's mapping is for. Nothing is
       * suppressed and nothing is thrown away.
       */
      /*
       * Nothing to adopt if the words are the same.
       *
       * Compared WITHOUT `h` and `rev`: those are the write guard's hash and
       * the revision a block was written at, and they change on every write
       * -- including one that leaves the text exactly as it was. Comparing
       * whole blocks therefore reports "different" for a revision a reader
       * could not see, and the editor then replaced the whole document to
       * adopt it. A full replace puts the selection back at the start, so the
       * caret of anybody who had just clicked into a paragraph was thrown
       * away and the next keystroke landed at the top of the document.
       *
       * That is BUG-029, and it is why it only ever showed under load: the
       * revision had to arrive in the moment between the click and the key.
       */
      if (JSON.stringify(visible(currentBlocks())) === JSON.stringify(visible(nextBlocks))) return
      const next = PMNode.fromJSON(blockSchema, { type: 'doc', content: nextBlocks })
      const tr = view.state.tr.replaceWith(0, view.state.doc.content.size, next.content)
      tr.setMeta('addToHistory', false)
      /*
       * Labelled, or the editor mistakes the server's own words for the
       * person's. `dispatchTransaction` marks any document change dirty,
       * queues a save and records every block it touched -- so adopting a
       * remote revision would schedule a write of the whole document, which
       * would come back as another revision, which would be adopted, which
       * would schedule another write.
       */
      tr.setMeta(REMOTE, true)
      view.dispatch(tr)
    })
  })

  /*
   * Keep the diagrams' tinting in step with the threads.
   *
   * Reads the store, so it re-runs when a thread is added, resolved or made
   * active -- and touches only the labels, never the diagram, so hovering a
   * comment does not redraw the picture.
   */
  $effect(() => {
    const active = hl?.active ?? null
    for (const [id, dv] of diagrams) dv.setComments(hl?.rangesFor(id) ?? [], active, hl)
  })

  onDestroy(() => {
    cancelAnimationFrame(chromeFrame)
    if (saveTimer) clearTimeout(saveTimer)
    view?.destroy()
    view = null
  })

  function run(cmd: ReturnType<typeof toggle>) {
    if (!view) return
    cmd(view.state, view.dispatch)
    view.focus()
    menuOpen = false
  }

  function linkPrompt() {
    if (!view) return
    if (marks.link) return run(setLink(null))
    const href = prompt('Link to:')
    if (href === null) return
    run(setLink(href.trim() || null))
  }

  const MARK_LABEL: Record<string, string> = {
    bold: 'Bold',
    italic: 'Italic',
    underline: 'Underline',
    strike: 'Strikethrough',
    code: 'Code',
  }
  const MARK_GLYPH: Record<string, string> = {
    bold: 'B',
    italic: 'I',
    underline: 'U',
    strike: 'S',
    code: '{ }',
  }

  const BLOCKS = [
    { label: 'Body text', run: () => run(setHeading(null)) },
    { label: 'Heading 1', run: () => run(setHeading(1)) },
    { label: 'Heading 2', run: () => run(setHeading(2)) },
    { label: 'Heading 3', run: () => run(setHeading(3)) },
    { label: 'Bulleted list', run: () => run(makeList('bullet')) },
    { label: 'Numbered list', run: () => run(makeList('ordered')) },
    { label: 'Checklist', run: () => run(makeList('check')) },
    { label: 'Quote', run: () => run(toggleQuote()) },
    { label: 'Code block', run: () => run(makeCodeBlock()) },
    { label: 'Divider', run: () => run(insertRule()) },
  ]

  const headingName = $derived(
    heading === 1 ? 'Heading 1' : heading === 2 ? 'Heading 2' : heading === 3 ? 'Heading 3' : 'Body text',
  )
</script>

<div class="doc-editor">
  <!-- Sticky, so the tools stay where they were put while the document
       scrolls under them. `mousedown|preventDefault` throughout: a button that
       takes focus blurs the editor, collapses the selection, and then acts on
       nothing. -->
  <div class="pm-bar" role="toolbar" aria-label="Formatting">
    <div class="pm-group">
      <button
        type="button"
        class="pm-tool pm-block-menu"
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        onmousedown={(e) => e.preventDefault()}
        onclick={() => (menuOpen = !menuOpen)}
      >
        {headingName}<span class="pm-caret" aria-hidden="true">▾</span>
      </button>
      {#if menuOpen}
        <ul class="pm-menu" role="menu">
          {#each BLOCKS as item (item.label)}
            <li role="none">
              <button
                type="button"
                role="menuitem"
                onmousedown={(e) => e.preventDefault()}
                onclick={item.run}>{item.label}</button
              >
            </li>
          {/each}
        </ul>
      {/if}
    </div>

    <span class="pm-sep" aria-hidden="true"></span>

    {#each TOGGLEABLE as name (name)}
      <button
        type="button"
        class="pm-tool"
        class:on={marks[name]}
        aria-pressed={marks[name] ? 'true' : 'false'}
        title={MARK_LABEL[name]}
        aria-label={MARK_LABEL[name]}
        onmousedown={(e) => e.preventDefault()}
        onclick={() => run(toggle(name as Toggleable))}>{MARK_GLYPH[name]}</button
      >
    {/each}
    <button
      type="button"
      class="pm-tool"
      class:on={marks.link}
      title={marks.link ? 'Remove link' : 'Add link'}
      aria-label={marks.link ? 'Remove link' : 'Add link'}
      onmousedown={(e) => e.preventDefault()}
      onclick={linkPrompt}>link</button
    >

    <span class="pm-spacer"></span>
    <span class="pm-state" aria-live="polite">
      {#if saving}Saving…{:else if dirty}Unsaved{:else}Saved{/if}
    </span>
  </div>

  {#if blockedBy}
    <p class="pm-blocked" role="status">
      {locks.holderOf(blockedBy) ?? 'Somebody'} is editing that paragraph. Your change was not applied.
    </p>
  {/if}

  <div class="pm-mount" bind:this={host}></div>
</div>

<style>
  .doc-editor {
    position: relative;
  }

  .pm-bar {
    position: sticky;
    /*
     * Stops exactly where the app's bar ends, which is sticky too.
     *
     * This carried its own guess of 3.25rem against the bar's 4.25rem, so as
     * soon as the page scrolled the toolbar slid under the bar and had its
     * top sheared off -- the buttons were there, cut in half. Both now read
     * `--below-topbar`, so the number cannot be right in one place and wrong
     * in the other.
     */
    top: var(--below-topbar);
    /* Below the app's bar (z-index 50) and above the document. */
    z-index: 5;
    display: flex;
    align-items: center;
    gap: 0.15rem;
    margin-bottom: 1rem;
    padding: 0.3rem 0.4rem;
    border: 1px solid var(--color-line);
    border-radius: 0.4rem;
    background: var(--color-paper);
  }

  .pm-group {
    position: relative;
  }

  .pm-sep {
    width: 1px;
    height: 1.1rem;
    margin: 0 0.35rem;
    background: var(--color-line);
  }

  .pm-spacer {
    flex: 1;
  }

  .pm-state {
    padding-right: 0.25rem;
    color: var(--color-ink-soft, inherit);
    font-family: var(--font-mono, monospace);
    font-size: 0.65rem;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    opacity: 0.7;
  }

  .pm-tool {
    min-width: 1.9rem;
    padding: 0.2rem 0.45rem;
    border: 1px solid transparent;
    border-radius: 0.25rem;
    background: transparent;
    color: inherit;
    font: inherit;
    font-size: 0.8rem;
    cursor: pointer;
  }

  .pm-tool:hover {
    background: var(--color-wash, #211d1810);
  }

  .pm-tool.on {
    border-color: var(--color-line);
    background: var(--color-wash, #211d1814);
    font-weight: 600;
  }

  .pm-block-menu {
    min-width: 7rem;
    text-align: left;
  }

  .pm-caret {
    margin-left: 0.3rem;
    opacity: 0.6;
  }

  .pm-menu {
    position: absolute;
    top: calc(100% + 0.25rem);
    left: 0;
    z-index: 10;
    min-width: 11rem;
    margin: 0;
    padding: 0.25rem;
    border: 1px solid var(--color-line);
    border-radius: 0.4rem;
    background: var(--color-paper);
    box-shadow: 0 10px 28px rgb(0 0 0 / 14%);
    list-style: none;
  }

  .pm-menu button {
    display: block;
    width: 100%;
    padding: 0.3rem 0.5rem;
    border: 0;
    border-radius: 0.25rem;
    background: transparent;
    color: inherit;
    font: inherit;
    font-size: 0.85rem;
    text-align: left;
    cursor: pointer;
  }

  .pm-menu button:hover {
    background: var(--color-wash, #211d1810);
  }

  .pm-blocked {
    margin: 0 0 0.75rem;
    color: var(--color-red-deep, var(--color-red));
    font-size: 0.85rem;
  }

  /* The editable surface is the document: same measure, same typography, no
     box around it. An editor that looks like a form is the thing this replaced. */
  .pm-mount :global(.pm-doc) {
    outline: none;
  }

  .pm-mount :global(.pm-doc [data-checked]) {
    list-style: none;
  }

  /* A diagram shows its picture until the caret is in it, and its source
     while it is being changed -- the source IS the content, so there is
     nothing else to edit. */
  .pm-mount :global(.pm-diagram) {
    position: relative;
  }

  .pm-mount :global(.pm-diagram-figure) {
    cursor: pointer;
  }

  .pm-mount :global(.pm-diagram-open .pm-diagram-source) {
    border-left: 2px solid var(--color-red);
    padding-left: 0.6rem;
  }
</style>
